/**
 * The datepicker component is a wrapper around the npm library react-day-picker
 * Documentation at https://react-day-picker.js.org/
 *
 */

import React, { useEffect, useState } from "react";
import "react-day-picker/dist/style.css";
import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";
import { IFlightDates, IRangeDates } from "redux/searchForm";
import { DayPicker } from "react-day-picker";
import { isBefore } from "date-fns";
import { LocaleKey, localeMap } from "./locales.interfaces";
import {
  daypickerDesktopSx,
  daypickerMobileSx,
  daypickerSx,
} from "./DatePickerStyles";
import { useConfig } from "@thekilroygroup/shared-ui";

interface IProps {
  isRange: boolean;
  selected: IFlightDates;
  fromMonth: Date;
  firstAvailableDate: Date;
  handleDateChange: (item: IFlightDates) => void;
  setOpen: (open: boolean) => void;
}

export function DateRangePicker(props: IProps) {
  const {
    isRange,
    selected,
    fromMonth,
    firstAvailableDate,
    handleDateChange,
    setOpen,
  } = props;

  const theme = useTheme();
  const { locale } = useConfig();
  const [selectedDatesState, setSelectedDatesState] = useState<IRangeDates>({
    departureDate: null,
    returnDate: null,
    entered_to_date: null,
  });
  const [highlighted_date, setHighlighted] = useState<Date | null>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    const nextDates = {
      departureDate:
        selected.departureDate !== null ? selected.departureDate : null,
      returnDate: selected.returnDate !== null ? selected.returnDate : null,
      entered_to_date:
        selected.returnDate !== null ? selected.returnDate : null,
    };
    setHighlighted(nextDates.entered_to_date);
    setSelectedDatesState(nextDates);
  }, [selected]);

  const { departureDate, returnDate } = selectedDatesState;
  const onewayTripModifier = { start: departureDate };
  const multiTripModifiers = {
    start: departureDate,
    end: highlighted_date,
  };
  const disabledDays = { before: firstAvailableDate };
  const selectedDays = { from: departureDate, to: highlighted_date };

  function isSelectingFirstDay(
    departureDate: Date,
    returnDate: Date | null,
    selected_date: Date,
  ) {
    const isBeforeFirstDay =
      departureDate && isBefore(selected_date, departureDate);
    const isRangeSelected = departureDate && returnDate;
    return !departureDate || isBeforeFirstDay || isRangeSelected;
  }

  function handleDayMouseEnter(selected_date: Date) {
    if (!isRange) {
      return;
    }
    if (!isSelectingFirstDay(departureDate, returnDate, selected_date)) {
      setHighlighted(selected_date);
    }
  }

  function handleDayFocus(e: React.FocusEvent<HTMLDivElement, Element>) {
    if (!isRange) {
      return;
    }

    const elm = e.target;
    const isDay =
      elm.classList.contains("DayPicker-Day") &&
      !elm.classList.contains("DayPicker-Day--disabled");
    const value = elm.getAttribute("aria-label");
    const date = new Date(value || "");

    if (isDay && !isSelectingFirstDay(departureDate, returnDate, date)) {
      setHighlighted(date);
    }
  }

  function handleDayClick(selected_d: Date) {
    const selected_date = new Date(selected_d.setHours(12));
    if (!isRange && String(selected.departureDate) == String(selected_date)) {
      setOpen(false);
      return;
    }
    if (!isRange) {
      const nextDates = {
        departureDate: selected_date,
        returnDate: null,
      };
      handleDateChange(nextDates);
      setOpen(false);
      return;
    }

    if (
      departureDate &&
      returnDate &&
      selected_date >= departureDate &&
      selected_date <= returnDate
    ) {
      const nextDates = {
        departureDate: null,
        returnDate: null,
      };

      handleDateChange(nextDates);
      return;
    }
    if (isSelectingFirstDay(departureDate, returnDate, selected_date)) {
      const nextDates = {
        departureDate: selected_date,
        returnDate: null,
      };

      handleDateChange(nextDates);
    } else {
      const nextDates = {
        departureDate: departureDate,
        returnDate: selected_date,
      };
      handleDateChange(nextDates);
      setOpen(false);
    }
  }

  return (
    <Box
      sx={
        {
          ...daypickerSx(theme),
          ...(isDesktop ? daypickerDesktopSx(theme) : daypickerMobileSx(theme)),
        } as SxProps
      }
    >
      <DayPicker
        className={`Selectable  `}
        numberOfMonths={isDesktop ? 2 : 24}
        selected={isRange ? selectedDays : departureDate}
        mode="range"
        modifiers={isRange ? multiTripModifiers : onewayTripModifier}
        onDayClick={handleDayClick}
        weekStartsOn={1}
        onDayMouseEnter={handleDayMouseEnter}
        onDayFocus={() => handleDayFocus}
        fromMonth={fromMonth}
        disabled={disabledDays}
        locale={localeMap[locale as LocaleKey]}
      />
    </Box>
  );
}
